import React, { useMemo, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Icon } from 'components/Icon';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Column, Header, Row } from 'types/Table';
import { ROW_DETAILS_TABLE_TOGGLE } from 'helpers/constants/table';

interface ColumnProps {
    column: Column | React.ReactNode;
    column_key: number;
    row: Row;
    headers?: Header[];
    rowClicked?: boolean | ((rowLink: Row['rowLink']) => void);
    setOpen: (value: boolean) => void;
    open: boolean;
}

const ColumnComponent = ({
    column,
    column_key,
    row,
    headers,
    rowClicked,
    setOpen,
    open,
}: ColumnProps) => {
    const columnKey = headers?.[column_key];

    const defaultLinkClicked = (link: (() => void) | string) => {
        if (typeof link === 'function') {
            // since in this component this.props.history
            // is undefined we have this as a workaround.
            link();
        }
    };

    if (typeof column === 'object' && 'tableHidden' in column && column.tableHidden === true) {
        return null;
    }
    
    if ((typeof columnKey === 'string') && columnKey === ROW_DETAILS_TABLE_TOGGLE) {
        return (
            <td key={column_key} className="text-center cursor-pointer">
                {row.rowDetails && (
                    <a href={void (0)} onClick={() => setOpen(!open)}>
                        <Icon size="1x" icon={open ? faChevronUp : faChevronDown}/>
                    </a>
                )}
            </td>
        );
    }

    let rowClick: (() => void) | undefined;

    if (row.rowLink) {
        rowClick = () => {
            if (rowClicked) {
                // use the user defined clicked function
                typeof rowClicked === 'function' && rowClicked(row.rowLink);
            } else {
                // use the default clicked function
                defaultLinkClicked(row.rowLink);
            }
        };
    }

    let body: React.ReactNode = ((column as Column).body || column) as React.ReactNode;

    if (typeof column === 'object' && 'body' in column) {
        // if key of body is in the object
        // then it's a user definied row
        body = column.body;
    }

    return (
        <td
            key={column_key}
            onClick={rowClick}
            className={typeof column === 'object' && 'className' in column ? column.className : 'text'}
        >
            {body}
        </td>
    );
};

interface ColumnsProps {
    results: Row[];
    headers?: Header[];
    rowClicked?: boolean | ((rowLink: Row['rowLink']) => void);
}

export const Columns = ({ results, ...props }: ColumnsProps) => {
    const { headers = [] } = props;

    const keyColIndex = useMemo(() => {
        return headers.findIndex(h => h.keyCol === true);
    }, [headers]);

    const [open, setOpen] = useState<{ [key: string]: boolean }>({});

    const handleSetOpen = (row_key: string, openedState = false) => {
        setOpen({ ...open, [row_key]: openedState });
    };

    const getId = (row: Row): string => {
        if (!keyColIndex || keyColIndex < 0) {
            return (row.columns[1] as { body?: string })?.body || '0';
        }
        return (row.columns[keyColIndex] as { body?: string })?.body || '0';
    };

    return (
        <tbody>
            {results.map((row, row_key) => {
                const id = getId(row);

                const { columns } = row;
                const rowOpen = open[id] || false;

                return (
                    <React.Fragment key={row_key}>
                        <tr
                            className={[row.rowLink ? 'clickable' : '', row.rowDetails && 'has-row-details'].join(' ')}
                        >
                            {columns.map((column, column_key) => (
                                <ColumnComponent
                                    key={column_key}
                                    column={column}
                                    column_key={column_key}
                                    row={row}
                                    setOpen={openedState => handleSetOpen(id, openedState)}
                                    open={rowOpen}
                                    {...props}
                                />
                            ))}
                        </tr>
                        {row.rowDetails &&
                        <Collapse in={rowOpen} timeout={0}>
                            <tr className="row-details">
                                <td colSpan={100} className="padding0">
                                    {row.rowDetails}
                                </td>
                            </tr>
                        </Collapse>
                        }
                    </React.Fragment>
                );
            })}
        </tbody>
    );
};